import { Badge, Button, Checkbox, cn, Input, NativeSelect, NativeSelectOption } from "@portal/ui";
import type { MemoScope, MemoSummary } from "@portal/schemas";
import {
  Archive,
  ArchiveRestore,
  Eye,
  PencilLine,
  Plus,
  Search,
  Trash2,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { updateMemoArchived } from "../../api/client";
import { useInvalidate, useMe, useMemos, usePrefetchMemo } from "../../api/queries";
import { preloadMarkdown, preloadMarkdownWhenIdle } from "../lazyMarkdown";
import { MEMO_ACCESS_LABELS } from "../../features/memos/memo-labels";
import { MemoDialog } from "../../features/memos/MemoDialog";
import {
  clearDraft,
  isNewDraft,
  memoIdOfDraft,
  newDraftId,
  useMemoDrafts,
  type StoredDraft,
} from "../../features/memos/memo-draft";
import { HomeSection } from "../HomeSection";
import { MemoHoverPreview } from "../../features/memos/MemoHoverPreview";
import { MemoIcon } from "../../features/memos/MemoIcon";
import { MemoBulkShareDialog } from "../../features/memos/MemoBulkShareDialog";
import { MemoRowMenu } from "../../features/memos/MemoRowMenu";
import { arrangeMemos } from "../../features/memos/memo-view";
import { MemoViewOptionsButton, useMemoViewOptions } from "../../features/memos/MemoViewOptions";
import { useUserName } from "../../features/memos/ShareUserPicker";
import { CardStatus } from "./CardStatus";

const when = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "Asia/Tokyo",
});

const SCOPE_LABELS: Record<MemoScope, string> = {
  all: "すべて",
  mine: "自分のメモ",
  shared: "共有されたメモ",
  archived: "アーカイブ",
};

/**
 * メモ(SPEC §13.5)。ホームから追加でき、一覧は更新の新しい順。検索と絞り込みはサーバー側で行う。
 * 基本は自分にだけ見え、共有されたメモは同じ一覧に「共有」のバッジ付きで出す。
 */
export function MemosCard({ className }: { className?: string }) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<MemoScope>("all");
  const [sheet, setSheet] = useState<{ open: boolean; id: string | null; draftId: string | null }>({
    open: false,
    id: null,
    draftId: null,
  });
  // 入力のたびに問い合わせないよう、少し待ってから検索する
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  // エディタは重いので、ホームが出たあとの空き時間に先読みしておく。開くときに待たせない
  useEffect(() => preloadMarkdownWhenIdle(), []);
  const prefetch = usePrefetchMemo();
  const memos = useMemos({ ...(q ? { q } : {}), scope });
  const rows = memos.data?.memos ?? [];

  // 下書き(ブラウザに残っている書きかけ)。新しいメモの下書きは複数あり、一覧の先頭に出す。
  // 既存のメモの下書きは、その行に「下書きあり」と出す。共有されたメモだけの表示では出さない
  const email = useMe().data?.user.email ?? null;
  const allDrafts = useMemoDrafts(email);
  const needle = q.toLowerCase();
  const newDrafts =
    scope === "shared" || scope === "archived"
      ? []
      : allDrafts.filter(
          (d) =>
            isNewDraft(d.draftId) &&
            (needle === "" || `${d.title}\n${d.body}`.toLowerCase().includes(needle)),
        );
  const draftedMemoIds = new Set(
    allDrafts.map((d) => memoIdOfDraft(d.draftId)).filter((id): id is string => id !== null),
  );

  // グループ・並び順・所有者での絞り込み(ユーザー要望 2026-10-15)。読み込んだ一覧の中で行う
  const [viewOptions, setViewOptions] = useMemoViewOptions();
  // 所有者は名前で出す(ユーザーマスタ。名前が無ければメール)
  const nameOf = useUserName();
  const ownerLabel = (ownerEmail: string) => nameOf(ownerEmail) ?? ownerEmail;
  const groups = arrangeMemos(rows, viewOptions, { me: email, nameOf, now: new Date() });
  const displayed = groups.flatMap((g) => g.memos);
  const owners = [...new Set(rows.map((m) => m.ownerEmail))]
    .map((o) => ({ email: o, label: o === email ? "自分" : ownerLabel(o) }))
    .sort((a, b) =>
      a.email === email ? -1 : b.email === email ? 1 : a.label.localeCompare(b.label, "ja"),
    );
  // 絞り込んでいる所有者が一覧からいなくなっても、選べるように残す
  if (viewOptions.owner && !owners.some((o) => o.email === viewOptions.owner))
    owners.push({ email: viewOptions.owner, label: ownerLabel(viewOptions.owner) });
  const filtering = q !== "" || scope !== "all" || viewOptions.owner !== null;

  // 複数選択(ユーザー要望 2026-10-15)。まとめて共有・アーカイブ(元に戻す)できるのは自分のメモだけなので、
  // 選べるのも自分のメモだけ。Shift を押しながら選ぶと、前に選んだ行からそこまでをまとめて選ぶ。
  // 検索や表示・所有者の絞り込みを変えたら選択を解く(見えないメモを操作しないように)。
  // 範囲で選ぶときは、画面に並んでいる順(並べ替え・グループのあと)で数える
  const view = `${scope}\u0000${q}\u0000${viewOptions.owner ?? ""}`;
  const [pick, setPick] = useState<{
    view: string;
    ids: ReadonlySet<string>;
    /** Shift で範囲を選ぶときの起点(最後に選んだ行)。 */
    anchor: string | null;
  }>({ view, ids: new Set(), anchor: null });
  const picked = pick.view === view ? pick.ids : new Set<string>();
  const setPicked = (ids: ReadonlySet<string>, anchor: string | null = null) =>
    setPick({ view, ids, anchor });
  const pickable = displayed.filter((m) => m.role === "OWNER");
  const selected = pickable.filter((m) => picked.has(m.id));
  const selecting = selected.length > 0;
  const clearSelection = () => setPicked(new Set());
  const togglePick = (id: string, range: boolean) => {
    const next = new Set(picked);
    const on = !next.has(id);
    const anchor = pick.view === view ? pick.anchor : null;
    const from = range && anchor ? displayed.findIndex((m) => m.id === anchor) : -1;
    const to = displayed.findIndex((m) => m.id === id);
    if (from >= 0 && to >= 0) {
      for (const m of displayed.slice(Math.min(from, to), Math.max(from, to) + 1)) {
        if (m.role !== "OWNER") continue;
        if (on) next.add(m.id);
        else next.delete(m.id);
      }
    } else if (on) next.add(id);
    else next.delete(id);
    setPicked(next, id);
  };
  const invalidate = useInvalidate();
  const [bulkSharing, setBulkSharing] = useState(false);
  const bulkArchive = useMutation({
    mutationFn: async (archived: boolean) => {
      const results = await Promise.allSettled(
        selected.map((m) => updateMemoArchived(m.id, { archived, revision: m.revision })),
      );
      return selected.filter((_, i) => results[i]?.status === "rejected");
    },
    onSuccess: (failed) => {
      void invalidate.memos();
      // 失敗したメモだけを選んだまま残す(もう一度押せる)
      setPicked(new Set(failed.map((m) => m.id)));
    },
  });
  return (
    <HomeSection
      data-testid="memos-card"
      className={className}
      // Esc で選択を解く(カードの中で押したときだけ。ダイアログの中の Esc はダイアログを閉じる)
      onKeyDown={(e) => {
        if (e.key === "Escape" && selecting && e.currentTarget.contains(e.target as Node))
          clearSelection();
      }}
      title="メモ"
      description="自分だけに見えるメモ。共有もできます"
      action={
        <Button
          size="sm"
          onPointerEnter={preloadMarkdown}
          onFocus={preloadMarkdown}
          // 押すたびに新しい下書きとして始める(書きかけの下書きは、一覧から選んで再開する)
          onClick={() => setSheet({ open: true, id: null, draftId: newDraftId() })}
        >
          <Plus />
          メモを追加
        </Button>
      }
    >
      {selecting ? (
        <div
          role="toolbar"
          aria-label="選択したメモの操作"
          className="relative flex h-9 items-center gap-2 rounded-md bg-muted px-2 text-sm"
        >
          {/* 「すべて選択」も、行のチェックと同じ左の余白の列に置く */}
          <span className="absolute inset-y-0 -left-6 flex w-6 items-center justify-center">
            <Checkbox
              aria-label="すべて選択"
              checked={selected.length === pickable.length}
              onCheckedChange={() =>
                setPicked(
                  selected.length === pickable.length
                    ? new Set()
                    : new Set(pickable.map((m) => m.id)),
                )
              }
            />
          </span>
          <span className="font-medium whitespace-nowrap">{selected.length} 件を選択中</span>
          {/* 3 列のときはカラムが狭いので、操作はアイコンにする(名前はホバーで出る) */}
          <span className="ml-auto flex shrink-0 items-center">
            {scope === "archived" ? (
              <Button
                size="icon"
                variant="ghost"
                disabled={bulkArchive.isPending}
                onClick={() => bulkArchive.mutate(false)}
                aria-label="元に戻す"
                title="元に戻す"
              >
                <ArchiveRestore />
              </Button>
            ) : (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="まとめて共有"
                  title="まとめて共有"
                  onClick={() => setBulkSharing(true)}
                >
                  <Users />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="アーカイブ"
                  title="アーカイブ"
                  disabled={bulkArchive.isPending}
                  onClick={() => bulkArchive.mutate(true)}
                >
                  <Archive />
                </Button>
              </>
            )}
            <Button
              size="icon"
              variant="ghost"
              aria-label="選択を解除"
              title="選択を解除 (Esc)"
              onClick={clearSelection}
            >
              <X />
            </Button>
          </span>
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search
              aria-hidden="true"
              className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              aria-label="メモを検索"
              placeholder="タイトルと本文を検索"
              className="pl-9"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
          <NativeSelect
            aria-label="表示するメモ"
            value={scope}
            onChange={(e) => setScope(e.target.value as MemoScope)}
          >
            {(Object.keys(SCOPE_LABELS) as MemoScope[]).map((s) => (
              <NativeSelectOption key={s} value={s}>
                {SCOPE_LABELS[s]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <MemoViewOptionsButton options={viewOptions} onChange={setViewOptions} owners={owners} />
        </div>
      )}
      {bulkArchive.isError && (
        <p role="alert" className="text-sm text-destructive">
          変更できませんでした。もう一度お試しください。
        </p>
      )}
      {(bulkArchive.data?.length ?? 0) > 0 && (
        <p role="alert" className="text-sm text-destructive">
          {bulkArchive.data?.length}{" "}
          件は変更できませんでした(ほかの人が同時に変えたなど)。選んだまま残しています。
        </p>
      )}
      <CardStatus
        isPending={memos.isPending}
        isError={memos.isError}
        refetch={() => memos.refetch()}
      />
      {memos.data && displayed.length === 0 && newDrafts.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {scope === "archived" && q === ""
            ? "アーカイブしたメモはありません。"
            : filtering
              ? "該当するメモはありません。"
              : "メモはまだありません。「メモを追加」から書けます。"}
        </p>
      )}
      {(displayed.length > 0 || newDrafts.length > 0) && (
        // 左に余白(-ml-6 pl-6)を取り、行の選択のチェックはそこに出す(行の中身は検索欄と左を揃える。
        // ユーザー要望 2026-10-15)。スクロールする枠の外に出すと切れるので、枠の中の余白に置く
        // 行の間に線は引かない(ユーザー要望 2026-10-15。行は乗せたときの色で見分ける)
        <ul className="-ml-6 flex max-h-96 flex-col overflow-y-auto pl-6 text-sm lg:max-h-none lg:min-h-0 lg:flex-1">
          {newDrafts.map((d) => (
            <DraftRow
              key={d.draftId}
              draft={d}
              onOpen={() => {
                preloadMarkdown();
                setSheet({ open: true, id: null, draftId: d.draftId });
              }}
              onDiscard={() => email && clearDraft(email, d.draftId)}
            />
          ))}
          {groups.map((g) => (
            <Fragment key={g.key}>
              {g.label !== null && g.memos.length > 0 && (
                // グループの見出し。スクロールしても上に残す
                <li
                  role="presentation"
                  data-testid="memo-group"
                  className="sticky top-0 z-10 flex items-center gap-1.5 bg-background pt-3 pb-1 text-xs font-medium text-muted-foreground"
                >
                  {g.label}
                  <span className="font-normal">{g.memos.length}</span>
                </li>
              )}
              {g.memos.map((m) => (
                <MemoRow
                  key={m.id}
                  memo={m}
                  ownerName={ownerLabel(m.ownerEmail)}
                  hasDraft={draftedMemoIds.has(m.id)}
                  selecting={selecting}
                  picked={picked.has(m.id)}
                  onPick={(range) => togglePick(m.id, range)}
                  onWarm={() => {
                    preloadMarkdown();
                    void prefetch(m.id);
                  }}
                  onOpen={() => setSheet({ open: true, id: m.id, draftId: null })}
                />
              ))}
            </Fragment>
          ))}
          {memos.data?.truncated && (
            <li className="py-2 text-xs text-muted-foreground">
              新しい 100 件を表示しています。検索で絞り込めます。
            </li>
          )}
        </ul>
      )}
      {email && (
        <MemoBulkShareDialog
          memos={selected}
          ownerEmail={email}
          open={bulkSharing}
          onOpenChange={setBulkSharing}
          onDone={() => {
            setBulkSharing(false);
            clearSelection();
          }}
        />
      )}
      <MemoDialog
        memoId={sheet.id}
        draftId={sheet.draftId}
        open={sheet.open}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
      />
    </HomeSection>
  );
}

/** 下書きの印。保存していない書きかけであることを示す。 */
function DraftBadge({ children }: { children: string }) {
  return (
    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900">
      {children}
    </Badge>
  );
}

/** 新しいメモの下書き。選ぶとその内容で再開する(手動の復元)。「破棄」で消す。 */
function DraftRow({
  draft,
  onOpen,
  onDiscard,
}: {
  draft: StoredDraft;
  onOpen: () => void;
  onDiscard: () => void;
}) {
  const title = draft.title || "無題のメモ";
  return (
    // 色は行全体(右の破棄のボタンまで)で変える
    <li className="flex items-center gap-1 rounded-sm hover:bg-muted/50">
      <MemoHoverPreview title={draft.title} icon={draft.icon ?? ""} body={draft.body}>
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 flex-1 flex-col gap-1 rounded-sm px-1 py-2 text-left"
        >
          <span className="flex items-center gap-2">
            <MemoIcon value={draft.icon ?? ""} />
            <span className="truncate font-medium">{title}</span>
            <DraftBadge>下書き</DraftBadge>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
              {when.format(new Date(draft.savedAt))}
            </span>
          </span>
        </button>
      </MemoHoverPreview>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`下書き「${title}」を破棄`}
        onClick={onDiscard}
      >
        <Trash2 />
      </Button>
    </li>
  );
}

function MemoRow({
  memo,
  ownerName,
  hasDraft,
  selecting,
  picked,
  onPick,
  onOpen,
  onWarm,
}: {
  memo: MemoSummary;
  /** 所有者の名前(名前が無ければメール)。共有されたメモに出す。 */
  ownerName: string;
  hasDraft: boolean;
  /** どれかを選んでいる間は、すべての行のチェックを見せる。 */
  selecting: boolean;
  picked: boolean;
  /** range: Shift を押しながら選んだ(前に選んだ行からまとめて選ぶ)。 */
  onPick: (range: boolean) => void;
  onOpen: () => void;
  onWarm: () => void;
}) {
  return (
    // 色は行全体(右の「⋮」まで)で変える。「⋮」のメニューを開いている間も残す(どの行の操作か分かるように)
    <li className="group relative flex items-center gap-1 rounded-sm hover:bg-muted/50 has-[[data-state=open]]:bg-muted/50">
      {/* 選べるのは自分のメモだけ(まとめて共有・アーカイブできるのは自分のメモだけ)。
          チェックは行の左の余白に出す。行とのあいだに隙間を作らず、乗せたまま左へ動かしても消えない */}
      {memo.role === "OWNER" && (
        <span className="absolute inset-y-0 -left-6 flex w-6 items-center justify-center">
          <Checkbox
            aria-label={`「${memo.title || "無題のメモ"}」を選択`}
            checked={picked}
            // Shift を見るため、クリックで切り替える(Radix の切り替えは止める)
            onClick={(e) => {
              e.preventDefault();
              onPick(e.shiftKey);
            }}
            className={cn(
              !selecting &&
                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=checked]:opacity-100",
            )}
          />
        </span>
      )}
      {/* 一覧には抜粋を出さず、乗せたときに本文を Markdown で見せる(ユーザー要望 2026-10-15) */}
      <MemoHoverPreview title={memo.title} icon={memo.icon} memoId={memo.id}>
        <button
          type="button"
          onClick={onOpen}
          onPointerEnter={onWarm}
          onFocus={onWarm}
          className="flex min-w-0 flex-1 flex-col gap-1 rounded-sm px-1 py-2 text-left"
        >
          <span className="flex items-center gap-2">
            <MemoIcon value={memo.icon} />
            <span className="truncate font-medium">{memo.title || "無題のメモ"}</span>
            {/* 共有は、Google ドライブのように題名の横の人のアイコンで示す(ユーザー要望 2026-10-15) */}
            {(memo.role !== "OWNER" || memo.shared) && (
              <RowIcon label={memo.role === "OWNER" ? "共有中" : "共有されたメモ"} icon={Users} />
            )}
            {hasDraft && <DraftBadge>下書きあり</DraftBadge>}
            {/* 権限も文字のバッジではなくアイコンで示す(閲覧のみは目、編集可は鉛筆。ユーザー要望 2026-10-15) */}
            {memo.role !== "OWNER" && (
              <RowIcon
                label={MEMO_ACCESS_LABELS[memo.role]}
                icon={memo.role === "VIEWER" ? Eye : PencilLine}
              />
            )}
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
              {memo.role !== "OWNER" && `${ownerName} · `}
              {when.format(new Date(memo.updatedAt))}
            </span>
          </span>
        </button>
      </MemoHoverPreview>
      <MemoRowMenu memo={memo} onOpen={onOpen} />
    </li>
  );
}

/** 題名の横の小さなアイコン(共有・権限)。名前はホバーで出し、読み上げにも渡す。 */
function RowIcon({ label, icon: Icon }: { label: string; icon: LucideIcon }) {
  return (
    <span role="img" aria-label={label} title={label} className="shrink-0 text-muted-foreground">
      <Icon aria-hidden="true" className="size-3.5" />
    </span>
  );
}
